// src/features/records/hooks/useRecordMutations.js
import { useCallback, useEffect, useRef, useState } from 'react';
import useEntityCache from '../stores/entityCache';
import { createRecord, promoteRecordDraft, updateRecord, deleteRecord } from '../services/core/mutations';
import { fetchRecordsByIds } from '../services/core/queries';

/**
 * Deep merge utility that preserves existing nested fields
 * @param {Object} target - The original object
 * @param {Object} patch - The updates to apply
 * @returns {Object} - Merged object with nested fields preserved
 */
function deepMergePreserve(target, patch) {
  if (patch === null || patch === undefined) return target;
  if (Array.isArray(patch) || Array.isArray(target)) return patch; // Replace arrays entirely
  if (typeof patch !== 'object' || typeof target !== 'object') return patch;

  const out = { ...target };
  for (const [k, v] of Object.entries(patch)) {
    out[k] = deepMergePreserve(target[k], v);
  }
  return out;
}

function useAsyncMutation({ mutationFn, onMutate, onSuccess, onError }) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const mutationIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setSafeState = useCallback((updater) => {
    if (!isMountedRef.current) return;
    updater();
  }, []);

  const mutateAsync = useCallback(
    async (variables, options = {}) => {
      const currentMutationId = ++mutationIdRef.current;
      let context;

      try {
        if (onMutate) {
          context = await onMutate(variables);
        }
      } catch (err) {
        setSafeState(() => {
          setStatus('error');
          setError(err);
        });
        if (options.onError) await options.onError(err, variables, context);
        if (onError) await onError(err, variables, context);
        throw err;
      }

      setSafeState(() => {
        setStatus('pending');
        setError(null);
      });

      try {
        const result = await mutationFn(variables);

        if (options.onSuccess) {
          await options.onSuccess(result, variables, context);
        }
        if (onSuccess) {
          await onSuccess(result, variables, context);
        }

        if (mutationIdRef.current === currentMutationId) {
          setSafeState(() => {
            setStatus('success');
          });
        }

        return result;
      } catch (err) {
        if (options.onError) {
          await options.onError(err, variables, context);
        }
        if (onError) {
          await onError(err, variables, context);
        }

        if (mutationIdRef.current === currentMutationId) {
          setSafeState(() => {
            setStatus('error');
            setError(err);
          });
        }

        throw err;
      }
    },
    [mutationFn, onMutate, onSuccess, onError, setSafeState]
  );

  const mutate = useCallback(
    (variables, options) => {
      mutateAsync(variables, options).catch(() => {
        // Errors are handled via callbacks to mirror React Query behaviour
      });
    },
    [mutateAsync]
  );

  const reset = useCallback(() => {
    setSafeState(() => {
      setStatus('idle');
      setError(null);
    });
  }, [setSafeState]);

  return {
    mutate,
    mutateAsync,
    reset,
    status,
    error,
    isIdle: status === 'idle',
    isPending: status === 'pending',
    isError: status === 'error',
    isSuccess: status === 'success',
  };
}

/**
 * Mutation hooks for records operations with Edge-Indexed architecture
 *
 * These hooks:
 * 1. Perform optimistic updates to the entity cache
 * 2. Call server APIs
 * 3. Let server-side edge projections handle list updates
 * 4. Handle rollback on errors
 *
 * Note: List invalidation is no longer needed as edge documents
 * are updated server-side automatically.
 */

/**
 * Hook for creating a new record
 *
 * Flow:
 * 1. Call server to create
 * 2. Add to entity cache for immediate access
 * 3. Edge documents are updated server-side automatically
 */
export function useCreateRecord() {
  const upsertMany = useEntityCache(state => state.upsertMany);
  
  return useAsyncMutation({
    mutationFn: async ({ formData, ai = null, sessionId = null, tempAttachments = null }) => {
      return createRecord(formData, ai, sessionId, tempAttachments);
    },
    onSuccess: (result) => {
      if (result?.success && result?.recordId) {
        void fetchRecordsByIds([result.recordId])
          .then((freshRecords) => {
            if (freshRecords?.length) {
              upsertMany(freshRecords);
            }
          })
          .catch((error) => {
            console.warn('Failed to fetch created record:', error);
          });
      }
    },
    onError: (error) => {
      console.error('Failed to create record:', error);
    }
  });
}

/**
 * Hook for promoting a record draft to a full record
 *
 * Flow:
 * 1. Call backend to promote draft using stored scope
 * 2. Fetch the created record for local cache hydration
 */
export function usePromoteRecordDraft() {
  const upsertMany = useEntityCache(state => state.upsertMany);

  return useAsyncMutation({
    mutationFn: async ({ draftId, overrides = null, sessionId = null, tempAttachments = null }) => {
      return promoteRecordDraft(draftId, overrides, sessionId, tempAttachments);
    },
    onSuccess: (result) => {
      if (result?.success && result?.recordId) {
        void fetchRecordsByIds([result.recordId])
          .then((freshRecords) => {
            if (freshRecords?.length) {
              upsertMany(freshRecords);
            }
          })
          .catch((error) => {
            console.warn('Failed to fetch promoted record:', error);
          });
      }
    },
    onError: (error) => {
      console.error('Failed to promote record draft:', error);
    }
  });
}

/**
 * Hook for updating an existing record
 *
 * Flow:
 * 1. Optimistically update entity cache
 * 2. Call server to update
 * 3. Update cache with server response
 * 4. Edge documents update automatically server-side
 */
export function useUpdateRecord() {
  const upsertMany = useEntityCache(state => state.upsertMany);
  const get = useEntityCache(state => state.get);
  
  return useAsyncMutation({
    mutationFn: async ({ recordId, updates, sessionId = null, tempAttachments = [] }) => {
      return updateRecord(recordId, updates, sessionId, tempAttachments);
    },
    onMutate: async ({ recordId, updates }) => {
      const previousRecord = get(recordId);

      if (previousRecord) {
        const optimisticRecord = deepMergePreserve(previousRecord, {
          ...updates,
          metadata: {
            ...(updates.metadata || {}),
            updatedAt: new Date()
          }
        });
        upsertMany([optimisticRecord]);
      }

      return { previousRecord, recordId };
    },
    onSuccess: async (result, variables) => {
      if (result?.success && variables.recordId) {
        try {
          const freshRecords = await fetchRecordsByIds([variables.recordId]);
          if (freshRecords && freshRecords.length > 0) {
            upsertMany(freshRecords);
          }
        } catch (error) {
          console.warn('Failed to fetch updated record:', error);
        }
      }
    },
    onError: (error, variables, context) => {
      console.error('Failed to update record:', error);

      if (context?.previousRecord) {
        upsertMany([context.previousRecord]);
      }
    }
  });
}

/**
 * Hook for deleting a record (soft delete)
 *
 * Flow:
 * 1. Optimistically remove from entity cache
 * 2. Call server to soft delete
 * 3. Edge documents are removed server-side automatically
 */
export function useDeleteRecord() {
  const get = useEntityCache(state => state.get);
  const upsertMany = useEntityCache(state => state.upsertMany);
  
  return useAsyncMutation({
    mutationFn: async (recordId) => {
      return deleteRecord(recordId);
    },
    onMutate: async (recordId) => {
      const previousRecord = get(recordId);
      return { previousRecord, recordId };
    },
    onError: (error, recordId, context) => {
      console.error('Failed to delete record:', error);

      if (context?.previousRecord) {
        upsertMany([context.previousRecord]);
      }
    }
  });
}

/**
 * Hook for setting task completion status
 * Specialized mutation for task view
 */
export function useToggleTaskComplete() {
  const updateMutation = useUpdateRecord();
  
  return useAsyncMutation({
    mutationFn: async ({ recordId, isCompleted }) => {
      const updates = {
        attributes: {
          isComplete: isCompleted
        }
      };

      return updateMutation.mutateAsync({ recordId, updates });
    }
  });
}

/**
 * Hook for bulk operations on records
 * Useful for multi-select actions
 */
export function useBulkUpdateRecords() {
  const upsertMany = useEntityCache(state => state.upsertMany);
  
  return useAsyncMutation({
    mutationFn: async ({ recordIds, updates, operation = 'update' }) => {
      if (operation === 'delete') {
        const promises = recordIds.map(id => deleteRecord(id));
        return Promise.all(promises);
      }

      const promises = recordIds.map(id => updateRecord(id, updates));
      return Promise.all(promises);
    },
    onSuccess: (results, variables) => {
      if (variables.operation === 'update') {
        const validResults = results.filter(r => r && r.id);
        if (validResults.length > 0) {
          upsertMany(validResults);
        }
      }
    },
    onError: (error) => {
      console.error('Bulk operation failed:', error);
    }
  });
}
